/**
 * Inventory — Manages unlocked Special Edition cards, Bullet Colors, and cosmetic statistics.
 * 
 * Persisted via StorageAdapter under the 'inventory' key.
 */

import { storageAdapter } from './storageAdapter.js';
import { CARDS_DATA } from '../data/cards.js';
import { MAX_EQUIPPED_TAUNTS } from '../data/taunts.js';
import { progress } from './progress.js';

export const BULLET_COLOR_EXCLUDED_UNITS = Object.freeze([
  'Reconsider',
  'The Salvage Company',
  'TheSalvageCompany',
  'Invasion Capsule',
  'Invasion Capsule Sling',
  'InvasionCapsuleSling',
  'InvasionCapsule',
  'Drone Command',
  'DroneCommand',
  'Wheelie Bomber',
  'WheelieBomber',
  'Hot Hand',
  'HotHand',
  'Hacker',
  'Defective Loaf',
  'DefectiveLoaf',
  'Rejected Loaf',
  'RejectedLoaf',
  'Harvesting Pillar',
  'HarvestingPillar',
  'Claiming Pillar',
  'ClaimingPillar',
  'Monitor Lock',
  'MonitorLock',
  'Parasite Vulture',
  'ParasiteVulture',
  'Anti Shade',
  'AntiShade',
  'Forgotten Loaf',
  'ForgottenLoaf'
]);

export function isBulletColorExcluded(unitName) {
  if (!unitName || typeof unitName !== 'string') return true;
  if (BULLET_COLOR_EXCLUDED_UNITS.includes(unitName)) return true;
  const lower = unitName.toLowerCase();
  return lower.includes('capsule') || lower.includes('wheelie') || lower.includes('hothand') || lower === 'hot hand' || lower === 'hacker';
}

export function isAuraExcluded(unitName) {
  if (!unitName || typeof unitName !== 'string') return true;
  const lower = unitName.toLowerCase();
  return lower.includes('reconsider') || lower.includes('capsule');
}

// Rocket trails are a per-unit cosmetic (like bullet colors and unit auras),
// but only units that actually propel a rocket have an exhaust plume to tint.
// Derived from the card data instead of a hardcoded name list so a future
// rocket unit picks the cosmetic up automatically - it must stay in sync with
// the runtime guard in Unit.getCustomRocketTrailHex().
let _rocketTrailUnitSet = null;

function getRocketTrailUnitSet() {
  if (!_rocketTrailUnitSet) {
    _rocketTrailUnitSet = new Set(
      CARDS_DATA
        .filter(c => c.enabled !== false && !c.isSpell && c.type !== 'Spell'
          && !isBulletColorExcluded(c.name)
          && (c.projectileType === 'Rocket' || c.dualMissile === true
              || c.unitType === 'Rocketship' || c.unitType === 'Orchestra'
              || c.name === 'Pink Beret' || c.unitType === 'PinkBeret'))
        .map(c => c.name)
    );
  }
  return _rocketTrailUnitSet;
}

export function isRocketTrailUnit(unitName) {
  if (!unitName || typeof unitName !== 'string') return false;
  return getRocketTrailUnitSet().has(unitName);
}

export const DEFAULT_BOOSTER_PACKS = 5;

export const STARTER_DECK_ICONS = Object.freeze([
  'generic-a',
  'generic-b',
  'generic-c',
  'generic-d',
  'human-crest',
  'sul-crest'
]);

class InventoryManager {
  constructor() {
    this._listeners = new Set();
    this._state = this._load();
    this._batchDepth = 0;
    this._batchDirty = false;
    this._notifyPending = false;
    // O(1) duplicate/ownership lookups regardless of how many units or
    // frame skins the roster grows to (Set of "unitName|styleId" / styleId
    // keys) instead of re-scanning the bulletColors/frames arrays on every
    // check - those arrays are consulted on every slot-machine spin, so a
    // linear .some() scan would get slower as the active roster (and the
    // cosmetics unlocked against it) grows, e.g. an 8x larger unit list.
    this._bulletColorSet = new Set(
      (this._state.bulletColors || []).map(b => `${b.unitName}|${b.styleId}`)
    );
    this._unitAuraSet = new Set(
      (this._state.unitAuras || []).map(a => `${a.unitName}|${a.styleId}`)
    );
    this._frameSet = new Set((this._state.frames || []).map(f => f.styleId));
    this._hpBarColorSet = new Set((this._state.hpBarColors || []).map(h => h.styleId));
    this._avatarBorderSet = new Set((this._state.avatarBorders || []).map(a => a.styleId));
    this._trayColorSet = new Set((this._state.trayColors || []).map(t => t.styleId));
    this._deckColorSet = new Set((this._state.deckColors || []).map(d => d.styleId));
    this._deckIconSet = new Set(this._state.deckIcons || STARTER_DECK_ICONS);
    this._deckbuilderThemeSet = new Set(this._state.unlockedDeckbuilderThemes || ['glass']);
    this._cardThemeSet = new Set(this._state.unlockedCardThemes || ['glass']);
    this._rocketTrailSet = new Set(
      (this._state.rocketTrails || []).map(r => `${r.unitName}|${r.styleId}`)
    );
    this._tauntSet = new Set(this._state.taunts || ['hello', 'gotchya', 'revenge']);
    this._profileArtworkSet = new Set(this._state.profileArtworks || ['Hyper Marine']);
    this._disabledSpecialCardsSet = new Set(
      Object.keys(this._state.disabledSpecialCards || {}).filter(k => this._state.disabledSpecialCards[k])
    );
    this._specialCardsSet = new Set(
      (this._state.specialCards || []).map(c => `${c.cardName}|${c.edition}`)
    );
  }

  reload() {
    this._state = this._load();
    this._bulletColorSet = new Set(
      (this._state.bulletColors || []).map(b => `${b.unitName}|${b.styleId}`)
    );
    this._unitAuraSet = new Set(
      (this._state.unitAuras || []).map(a => `${a.unitName}|${a.styleId}`)
    );
    this._frameSet = new Set((this._state.frames || []).map(f => f.styleId));
    this._hpBarColorSet = new Set((this._state.hpBarColors || []).map(h => h.styleId));
    this._avatarBorderSet = new Set((this._state.avatarBorders || []).map(a => a.styleId));
    this._trayColorSet = new Set((this._state.trayColors || []).map(t => t.styleId));
    this._deckColorSet = new Set((this._state.deckColors || []).map(d => d.styleId));
    this._deckIconSet = new Set(this._state.deckIcons || STARTER_DECK_ICONS);
    this._deckbuilderThemeSet = new Set(this._state.unlockedDeckbuilderThemes || ['glass']);
    this._cardThemeSet = new Set(this._state.unlockedCardThemes || ['glass']);
    this._rocketTrailSet = new Set(
      (this._state.rocketTrails || []).map(r => `${r.unitName}|${r.styleId}`)
    );
    this._tauntSet = new Set(this._state.taunts || ['hello', 'gotchya', 'revenge']);
    this._profileArtworkSet = new Set(this._state.profileArtworks || ['Hyper Marine']);
    this._disabledSpecialCardsSet = new Set(
      Object.keys(this._state.disabledSpecialCards || {}).filter(k => this._state.disabledSpecialCards[k])
    );
    this._specialCardsSet = new Set(
      (this._state.specialCards || []).map(c => `${c.cardName}|${c.edition}`)
    );
    this._notify();
  }

  _load() {
    const raw = storageAdapter.get('inventory', null);
    if (!raw || !Array.isArray(raw.specialCards)) {
      const initial = {
        specialCards: [],
        bulletColors: [],
        equippedBulletColors: {},
        unitAuras: [],
        equippedUnitAuras: {},
        frames: [],
        equippedFrame: null,
        hpBarColors: [],
        equippedHpBarColor: null,
        avatarBorders: [],
        equippedAvatarBorder: null,
        rocketTrails: [],
        equippedRocketTrails: {},
        trayColors: [],
        equippedTrayColor: null,
        deckColors: [],
        equippedDeckColor: null,
        deckIcons: [...STARTER_DECK_ICONS],
        equippedDeckIcon: 'generic-a',
        deckbuilderTheme: 'glass',
        unlockedDeckbuilderThemes: ['glass'],
        cardTheme: 'glass',
        unlockedCardThemes: ['glass'],
        disabledSpecialCards: {},
        taunts: ['hello', 'gotchya', 'revenge'],
        equippedTaunts: ['hello', 'gotchya', 'revenge'],
        profileArtworks: ['Hyper Marine'],
        equippedProfileArtwork: 'Hyper Marine',
        boosterPacks: DEFAULT_BOOSTER_PACKS,
        stats: {
          packsOpened: 0,
          ticketsFound: 0,
          specialCardsFound: 0,
          slotsSpun: 0,
          duplicatesRefunded: 0
        },
        version: 15
      };
      storageAdapter.set('inventory', initial);
      return initial;
    }

    // Migration to version 2 (Bullet Colors & Slot Stats)
    let dirty = false;
    if (!Array.isArray(raw.bulletColors)) {
      raw.bulletColors = [];
      dirty = true;
    }
    if (!raw.equippedBulletColors || typeof raw.equippedBulletColors !== 'object') {
      raw.equippedBulletColors = {};
      dirty = true;
    }
    if (!raw.stats) {
      raw.stats = {};
      dirty = true;
    }
    if (typeof raw.stats.slotsSpun !== 'number') {
      raw.stats.slotsSpun = 0;
      raw.stats.duplicatesRefunded = 0;
      dirty = true;
    }
    // Clean up any legacy entries for non-combat / non-projectile cards (Reconsider, The Salvage Company, Invasion Capsule, Wheelie Bomber)
    if (Array.isArray(raw.bulletColors)) {
      const beforeLen = raw.bulletColors.length;
      raw.bulletColors = raw.bulletColors.filter(b => !isBulletColorExcluded(b.unitName));
      if (raw.bulletColors.length !== beforeLen) dirty = true;
    }
    if (raw.equippedBulletColors) {
      for (const name of Object.keys(raw.equippedBulletColors)) {
        if (isBulletColorExcluded(name)) {
          delete raw.equippedBulletColors[name];
          dirty = true;
        }
      }
    }

    // Migration to version 3 (global Card Frame skins - Slotmachine "Spieler x Rahmen x Farbe")
    if (!Array.isArray(raw.frames)) {
      raw.frames = [];
      dirty = true;
    }
    if (raw.equippedFrame === undefined) {
      raw.equippedFrame = null;
      dirty = true;
    }

    // Migration to version 5 (default 5 booster packs)
    if (typeof raw.boosterPacks !== 'number') {
      raw.boosterPacks = DEFAULT_BOOSTER_PACKS;
      dirty = true;
    } else if ((raw.version || 0) < 5) {
      if (!raw.stats?.packsOpened && raw.boosterPacks === 1) {
        raw.boosterPacks = DEFAULT_BOOSTER_PACKS;
      } else {
        raw.boosterPacks = Math.max(raw.boosterPacks, DEFAULT_BOOSTER_PACKS);
      }
      dirty = true;
    }

    // Migration to version 6 (Unit Auras, Player HP Bar Colors, Player Avatar Borders)
    if (!Array.isArray(raw.unitAuras)) {
      raw.unitAuras = [];
      dirty = true;
    }
    if (!raw.equippedUnitAuras || typeof raw.equippedUnitAuras !== 'object') {
      raw.equippedUnitAuras = {};
      dirty = true;
    }
    if (Array.isArray(raw.unitAuras)) {
      const beforeLen = raw.unitAuras.length;
      raw.unitAuras = raw.unitAuras.filter(a => !isAuraExcluded(a.unitName));
      if (raw.unitAuras.length !== beforeLen) dirty = true;
    }
    if (raw.equippedUnitAuras) {
      for (const name of Object.keys(raw.equippedUnitAuras)) {
        if (isAuraExcluded(name)) {
          delete raw.equippedUnitAuras[name];
          dirty = true;
        }
      }
    }
    if (!Array.isArray(raw.hpBarColors)) {
      raw.hpBarColors = [];
      dirty = true;
    }
    if (raw.equippedHpBarColor === undefined) {
      raw.equippedHpBarColor = null;
      dirty = true;
    }
    if (!Array.isArray(raw.avatarBorders)) {
      raw.avatarBorders = [];
      dirty = true;
    }
    if (raw.equippedAvatarBorder === undefined) {
      raw.equippedAvatarBorder = null;
      dirty = true;
    }

    // Migration to version 7 (Player Taunts / Emotes)
    if (!Array.isArray(raw.taunts)) {
      raw.taunts = ['hello', 'gotchya', 'revenge'];
      dirty = true;
    }
    if (!Array.isArray(raw.equippedTaunts)) {
      raw.equippedTaunts = ['hello', 'gotchya', 'revenge'];
      dirty = true;
    }

    // Migration to version 8 (Rocket Trails)
    if (!Array.isArray(raw.rocketTrails)) {
      raw.rocketTrails = [];
      dirty = true;
    }
    if (!raw.equippedRocketTrails || typeof raw.equippedRocketTrails !== 'object') {
      raw.equippedRocketTrails = {};
      dirty = true;
    }

    // Migration to version 9 (Rocket Trails: global player cosmetic -> per-unit).
    // Version 8 stored one account-wide trail (`rocketTrails: [{ styleId }]` +
    // `equippedRocketTrail`). Every style the player already owned is handed to
    // each rocket-capable unit, and the previously equipped style is re-equipped
    // on all of them, so the battlefield looks exactly as it did before.
    const rocketUnitNames = [...getRocketTrailUnitSet()];
    const legacyTrails = raw.rocketTrails.filter(r => !r.unitName);
    if (legacyTrails.length > 0) {
      raw.rocketTrails = raw.rocketTrails.filter(r => !!r.unitName);
      for (const trail of legacyTrails) {
        for (const unitName of rocketUnitNames) {
          if (raw.rocketTrails.some(r => r.unitName === unitName && r.styleId === trail.styleId)) continue;
          raw.rocketTrails.push({ ...trail, unitName });
        }
      }
      dirty = true;
    }
    if (typeof raw.equippedRocketTrail === 'string' && raw.equippedRocketTrail) {
      for (const unitName of rocketUnitNames) {
        if (raw.equippedRocketTrails[unitName]) continue;
        raw.equippedRocketTrails[unitName] = raw.equippedRocketTrail;
      }
    }
    if (raw.equippedRocketTrail !== undefined) {
      delete raw.equippedRocketTrail;
      dirty = true;
    }
    // Drop unlocks/equips for units that can no longer carry a rocket trail
    // (roster changes, or a unit that lost its rocket propulsion).
    const beforeTrailLen = raw.rocketTrails.length;
    raw.rocketTrails = raw.rocketTrails.filter(r => isRocketTrailUnit(r.unitName));
    if (raw.rocketTrails.length !== beforeTrailLen) dirty = true;
    for (const name of Object.keys(raw.equippedRocketTrails)) {
      if (!isRocketTrailUnit(name)) {
        delete raw.equippedRocketTrails[name];
        dirty = true;
      }
    }

    // Migration to version 10 (Card Tray Glow - recessed hand-dock background color)
    if (!Array.isArray(raw.trayColors)) {
      raw.trayColors = [];
      dirty = true;
    }
    if (raw.equippedTrayColor === undefined) {
      raw.equippedTrayColor = null;
      dirty = true;
    }

    // Migration to version 11 (Special Edition card toggle / disabledSpecialCards)
    if (!raw.disabledSpecialCards || typeof raw.disabledSpecialCards !== 'object') {
      raw.disabledSpecialCards = {};
      dirty = true;
    }

    // Migration to version 12 (Deck Colors)
    if (!Array.isArray(raw.deckColors)) {
      raw.deckColors = [];
      dirty = true;
    }
    if (raw.equippedDeckColor === undefined) {
      raw.equippedDeckColor = null;
      dirty = true;
    }

    // Migration to version 13 (Profile Artworks)
    if (!Array.isArray(raw.profileArtworks)) {
      raw.profileArtworks = ['Hyper Marine'];
      dirty = true;
    } else if (raw.profileArtworks.length === 0) {
      raw.profileArtworks.push('Hyper Marine');
      dirty = true;
    }
    if (raw.equippedProfileArtwork === undefined) {
      raw.equippedProfileArtwork = 'Hyper Marine';
      dirty = true;
    }

    // Migration to version 14 (Deckbuilder Card Chassis Themes)
    if (raw.deckbuilderTheme === undefined) {
      raw.deckbuilderTheme = 'glass';
      dirty = true;
    }
    if (!Array.isArray(raw.unlockedDeckbuilderThemes)) {
      raw.unlockedDeckbuilderThemes = ['glass'];
      dirty = true;
    }

    // Migration to version 16 (In-Game Hand Card Chassis Themes - "Cards")
    if (raw.cardTheme === undefined) {
      raw.cardTheme = 'glass';
      dirty = true;
    }
    if (!Array.isArray(raw.unlockedCardThemes)) {
      raw.unlockedCardThemes = ['glass'];
      dirty = true;
    }

    // Migration to version 15 (Deck Icons)
    if (!Array.isArray(raw.deckIcons)) {
      raw.deckIcons = [...STARTER_DECK_ICONS];
      dirty = true;
    } else {
      for (const starter of STARTER_DECK_ICONS) {
        if (!raw.deckIcons.includes(starter)) {
          raw.deckIcons.push(starter);
          dirty = true;
        }
      }
    }
    if (raw.equippedDeckIcon === undefined || !raw.equippedDeckIcon) {
      raw.equippedDeckIcon = 'generic-a';
      dirty = true;
    }

    if (dirty || (raw.version || 0) < 15) {
      raw.version = 15;
      storageAdapter.set('inventory', raw);
    }
    return raw;
  }

  _save() {
    if (this._batchDepth > 0) {
      this._batchDirty = true;
      return;
    }
    storageAdapter.set('inventory', this._state);
    this._notifyPending = false;
    this._notify();
  }

  // See WalletManager.beginBatch(): one storage write + one notification per batch.
  beginBatch() {
    this._batchDepth = (this._batchDepth || 0) + 1;
  }

  endBatch({ notify = true } = {}) {
    if (!this._batchDepth) return;
    if (--this._batchDepth > 0 || !this._batchDirty) return;
    this._batchDirty = false;
    storageAdapter.set('inventory', this._state);
    if (notify) {
      this._notifyPending = false;
      this._notify();
    } else {
      this._notifyPending = true;
    }
  }

  flushNotify() {
    if (!this._notifyPending) return;
    this._notifyPending = false;
    this._notify();
  }

  _notify() {
    for (const listener of this._listeners) {
      try { listener(this._state); } catch (err) { console.error(err); }
    }
  }

  addListener(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  onChange(fn) {
    return this.addListener(fn);
  }

  // --- Special Edition Cards ---
  getSpecialCards() {
    return [...this._state.specialCards];
  }

  hasSpecialCard(cardName, edition = 'gold') {
    return this._state.specialCards.some(
      c => c.cardName === cardName && c.edition === edition
    );
  }

  isSpecialCardActive(cardName, edition = 'gold') {
    if (!this.hasSpecialCard(cardName, edition)) return false;
    return !this._disabledSpecialCardsSet.has(`${cardName}|${edition}`);
  }

  setSpecialCardActive(cardName, edition = 'gold', active = true) {
    if (!this.hasSpecialCard(cardName, edition)) return false;
    if (!this._state.disabledSpecialCards || typeof this._state.disabledSpecialCards !== 'object') {
      this._state.disabledSpecialCards = {};
    }
    const key = `${cardName}|${edition}`;
    if (active) {
      delete this._state.disabledSpecialCards[key];
      this._disabledSpecialCardsSet.delete(key);
    } else {
      this._state.disabledSpecialCards[key] = true;
      this._disabledSpecialCardsSet.add(key);
    }
    this._save();
    return true;
  }

  toggleSpecialCard(cardName, edition = 'gold') {
    if (!this.hasSpecialCard(cardName, edition)) return false;
    const currentlyActive = this.isSpecialCardActive(cardName, edition);
    this.setSpecialCardActive(cardName, edition, !currentlyActive);
    return !currentlyActive;
  }

  addSpecialCard(cardName, edition = 'gold') {
    if (this.hasSpecialCard(cardName, edition)) {
      return false; // Already owned
    }
    this._state.specialCards.push({
      cardName,
      edition,
      unlockedAt: new Date().toISOString()
    });
    this._specialCardsSet.add(`${cardName}|${edition}`);
    this._state.stats.specialCardsFound = (this._state.stats.specialCardsFound || 0) + 1;
    this._save();
    progress.recordUniquePerkUnlocked();
    return true;
  }

  // --- Bullet Colors (Phase 4 & 5) ---
  getBulletColors() {
    return [...(this._state.bulletColors || [])];
  }

  hasBulletColor(unitName, styleId) {
    return this._bulletColorSet.has(`${unitName}|${styleId}`);
  }

  addBulletColor(unitName, styleId, styleName = '', hex = '') {
    if (isBulletColorExcluded(unitName)) {
      return false; // Spells, unarmed units, drop pods, and kamikaze units do not have bullet colors
    }
    if (this.hasBulletColor(unitName, styleId)) {
      return false; // Already owned
    }
    if (!Array.isArray(this._state.bulletColors)) {
      this._state.bulletColors = [];
    }
    this._state.bulletColors.push({
      unitName,
      styleId,
      styleName,
      hex,
      unlockedAt: new Date().toISOString()
    });
    this._bulletColorSet.add(`${unitName}|${styleId}`);
    // Auto-equip if first cosmetic for this unit
    if (!this._state.equippedBulletColors) this._state.equippedBulletColors = {};
    if (!this._state.equippedBulletColors[unitName]) {
      this._state.equippedBulletColors[unitName] = styleId;
    }
    this._save();
    progress.recordUniquePerkUnlocked();
    return true;
  }

  equipBulletColor(unitName, styleId) {
    if (isBulletColorExcluded(unitName)) return;
    if (!this._state.equippedBulletColors) this._state.equippedBulletColors = {};
    this._state.equippedBulletColors[unitName] = styleId;
    this._save();
  }

  getEquippedBulletColor(unitName) {
    if (isBulletColorExcluded(unitName)) return null;
    return this._state.equippedBulletColors?.[unitName] || null;
  }

  // --- Card Frames (global cosmetic - Slotmachine category "Spieler x Rahmen x Farbe") ---
  // Unlike Bullet Colors, a frame skin is not owned per-unit: one spin result
  // unlocks one global skin the player can equip account-wide, so this stays
  // O(1) in both storage and lookup no matter how large the unit roster gets.
  getFrames() {
    return [...(this._state.frames || [])];
  }

  hasFrame(styleId) {
    return this._frameSet.has(styleId);
  }

  addFrame(styleId, styleName = '', hex = '') {
    if (this.hasFrame(styleId)) {
      return false; // Already owned
    }
    if (!Array.isArray(this._state.frames)) {
      this._state.frames = [];
    }
    this._state.frames.push({
      styleId,
      styleName,
      hex,
      unlockedAt: new Date().toISOString()
    });
    this._frameSet.add(styleId);
    // Auto-equip the very first frame skin ever unlocked
    if (!this._state.equippedFrame) {
      this._state.equippedFrame = styleId;
    }
    this._save();
    progress.recordUniquePerkUnlocked();
    return true;
  }

  equipFrame(styleId) {
    if (styleId && !this.hasFrame(styleId)) return;
    this._state.equippedFrame = styleId || null;
    this._save();
  }

  getEquippedFrame() {
    return this._state.equippedFrame || null;
  }

  // --- Unit Auras (Ground Aura & Underglow) ---
  getUnitAuras() {
    return [...(this._state.unitAuras || [])];
  }

  hasUnitAura(unitName, styleId) {
    return this._unitAuraSet.has(`${unitName}|${styleId}`);
  }

  addUnitAura(unitName, styleId, styleName = '', hex = '') {
    if (isAuraExcluded(unitName)) {
      return false;
    }
    if (this.hasUnitAura(unitName, styleId)) {
      return false;
    }
    if (!Array.isArray(this._state.unitAuras)) {
      this._state.unitAuras = [];
    }
    this._state.unitAuras.push({
      unitName,
      styleId,
      styleName,
      hex,
      unlockedAt: new Date().toISOString()
    });
    this._unitAuraSet.add(`${unitName}|${styleId}`);
    if (!this._state.equippedUnitAuras) this._state.equippedUnitAuras = {};
    if (!this._state.equippedUnitAuras[unitName]) {
      this._state.equippedUnitAuras[unitName] = styleId;
    }
    this._save();
    progress.recordUniquePerkUnlocked();
    return true;
  }

  equipUnitAura(unitName, styleId) {
    if (isAuraExcluded(unitName)) return;
    if (!this._state.equippedUnitAuras) this._state.equippedUnitAuras = {};
    this._state.equippedUnitAuras[unitName] = styleId || null;
    this._save();
  }

  getEquippedUnitAura(unitName) {
    if (isAuraExcluded(unitName)) return null;
    return this._state.equippedUnitAuras?.[unitName] || null;
  }

  // --- Player HP Bar Colors ---
  getHpBarColors() {
    return [...(this._state.hpBarColors || [])];
  }

  hasHpBarColor(styleId) {
    return this._hpBarColorSet.has(styleId);
  }

  addHpBarColor(styleId, styleName = '', hex = '') {
    if (this.hasHpBarColor(styleId)) {
      return false;
    }
    if (!Array.isArray(this._state.hpBarColors)) {
      this._state.hpBarColors = [];
    }
    this._state.hpBarColors.push({
      styleId,
      styleName,
      hex,
      unlockedAt: new Date().toISOString()
    });
    this._hpBarColorSet.add(styleId);
    if (!this._state.equippedHpBarColor) {
      this._state.equippedHpBarColor = styleId;
    }
    this._save();
    progress.recordUniquePerkUnlocked();
    return true;
  }

  equipHpBarColor(styleId) {
    if (styleId && !this.hasHpBarColor(styleId)) return;
    this._state.equippedHpBarColor = styleId || null;
    this._save();
  }

  getEquippedHpBarColor() {
    return this._state.equippedHpBarColor || null;
  }

  // --- Player Avatar Borders ---
  getAvatarBorders() {
    return [...(this._state.avatarBorders || [])];
  }

  hasAvatarBorder(styleId) {
    return this._avatarBorderSet.has(styleId);
  }

  addAvatarBorder(styleId, styleName = '', hex = '') {
    if (this.hasAvatarBorder(styleId)) {
      return false;
    }
    if (!Array.isArray(this._state.avatarBorders)) {
      this._state.avatarBorders = [];
    }
    this._state.avatarBorders.push({
      styleId,
      styleName,
      hex,
      unlockedAt: new Date().toISOString()
    });
    this._avatarBorderSet.add(styleId);
    if (!this._state.equippedAvatarBorder) {
      this._state.equippedAvatarBorder = styleId;
    }
    this._save();
    progress.recordUniquePerkUnlocked();
    return true;
  }

  equipAvatarBorder(styleId) {
    if (styleId && !this.hasAvatarBorder(styleId)) return;
    this._state.equippedAvatarBorder = styleId || null;
    this._save();
  }

  getEquippedAvatarBorder() {
    return this._state.equippedAvatarBorder || null;
  }

  // --- Card Tray Glow (global cosmetic - recessed hand-dock background) ---
  getTrayColors() {
    return [...(this._state.trayColors || [])];
  }

  hasTrayColor(styleId) {
    return this._trayColorSet.has(styleId);
  }

  addTrayColor(styleId, styleName = '', hex = '') {
    if (this.hasTrayColor(styleId)) {
      return false;
    }
    if (!Array.isArray(this._state.trayColors)) {
      this._state.trayColors = [];
    }
    this._state.trayColors.push({
      styleId,
      styleName,
      hex,
      unlockedAt: new Date().toISOString()
    });
    this._trayColorSet.add(styleId);
    if (!this._state.equippedTrayColor) {
      this._state.equippedTrayColor = styleId;
    }
    this._save();
    progress.recordUniquePerkUnlocked();
    return true;
  }

  equipTrayColor(styleId) {
    if (styleId && !this.hasTrayColor(styleId)) return;
    this._state.equippedTrayColor = styleId || null;
    this._save();
  }

  getEquippedTrayColor() {
    return this._state.equippedTrayColor || null;
  }

  // --- Deck Colors (global cosmetic - Deck Accent & Theme in Deck Selection) ---
  getDeckColors() {
    return [...(this._state.deckColors || [])];
  }

  hasDeckColor(styleId) {
    return this._deckColorSet.has(styleId);
  }

  addDeckColor(styleId, styleName = '', hex = '') {
    if (this.hasDeckColor(styleId)) {
      return false; // Already owned
    }
    if (!Array.isArray(this._state.deckColors)) {
      this._state.deckColors = [];
    }
    this._state.deckColors.push({
      styleId,
      styleName,
      hex,
      unlockedAt: new Date().toISOString()
    });
    this._deckColorSet.add(styleId);
    if (!this._state.equippedDeckColor) {
      this._state.equippedDeckColor = styleId;
    }
    this._save();
    progress.recordUniquePerkUnlocked();
    return true;
  }

  equipDeckColor(styleId) {
    if (styleId && !this.hasDeckColor(styleId)) return;
    this._state.equippedDeckColor = styleId || null;
    this._save();
  }

  getEquippedDeckColor() {
    return this._state.equippedDeckColor || null;
  }

  // --- Deckbuilder Card Themes (Cyber Glass vs Classic Steel) ---
  getDeckbuilderThemes() {
    return [...(this._state.unlockedDeckbuilderThemes || ['glass'])];
  }

  hasDeckbuilderTheme(themeId) {
    return this._deckbuilderThemeSet.has(themeId);
  }

  addDeckbuilderTheme(themeId) {
    if (this.hasDeckbuilderTheme(themeId)) {
      return false; // Already owned
    }
    if (!Array.isArray(this._state.unlockedDeckbuilderThemes)) {
      this._state.unlockedDeckbuilderThemes = ['glass'];
    }
    this._state.unlockedDeckbuilderThemes.push(themeId);
    this._deckbuilderThemeSet.add(themeId);
    this._save();
    progress.recordUniquePerkUnlocked();
    return true;
  }

  equipDeckbuilderTheme(themeId) {
    if (themeId && !this.hasDeckbuilderTheme(themeId)) return false;
    this._state.deckbuilderTheme = themeId || 'glass';
    this._save();
    return true;
  }

  getEquippedDeckbuilderTheme() {
    return this._state.deckbuilderTheme || 'glass';
  }

  // --- In-Game Hand Card Themes ("Cards" - Cyber Glass, Fluted, Holo, Classic Steel, Artwork) ---
  getCardThemes() {
    return [...(this._state.unlockedCardThemes || ['glass'])];
  }

  hasCardTheme(themeId) {
    return this._cardThemeSet.has(themeId);
  }

  addCardTheme(themeId) {
    if (this.hasCardTheme(themeId)) {
      return false; // Already owned
    }
    if (!Array.isArray(this._state.unlockedCardThemes)) {
      this._state.unlockedCardThemes = ['glass'];
    }
    this._state.unlockedCardThemes.push(themeId);
    this._cardThemeSet.add(themeId);
    this._save();
    progress.recordUniquePerkUnlocked();
    return true;
  }

  equipCardTheme(themeId) {
    if (themeId && !this.hasCardTheme(themeId)) return false;
    this._state.cardTheme = themeId || 'glass';
    this._save();
    return true;
  }

  getEquippedCardTheme() {
    return this._state.cardTheme || 'glass';
  }

  // --- Deck Icons (global cosmetic & deckbuilder presets - Faction Crests & Insignia) ---
  getDeckIcons() {
    return [...(this._state.deckIcons || STARTER_DECK_ICONS)];
  }

  hasDeckIcon(iconId) {
    if (!iconId) return false;
    if (STARTER_DECK_ICONS.includes(iconId)) return true;
    return this._deckIconSet.has(iconId);
  }

  addDeckIcon(iconId, label = '', rarity = 'rare') {
    if (!iconId || this.hasDeckIcon(iconId)) {
      return false; // Already owned
    }
    if (!Array.isArray(this._state.deckIcons)) {
      this._state.deckIcons = [...STARTER_DECK_ICONS];
    }
    this._state.deckIcons.push(iconId);
    this._deckIconSet.add(iconId);
    if (!this._state.equippedDeckIcon) {
      this._state.equippedDeckIcon = iconId;
    }
    this._save();
    progress.recordUniquePerkUnlocked();
    return true;
  }

  equipDeckIcon(iconId) {
    if (iconId && !this.hasDeckIcon(iconId)) return false;
    this._state.equippedDeckIcon = iconId || 'generic-a';
    this._save();
    return true;
  }

  getEquippedDeckIcon() {
    return this._state.equippedDeckIcon || 'generic-a';
  }

  // --- Rocket Trails (per-unit, only for rocket-propelled units) ---
  getRocketTrails() {
    return [...(this._state.rocketTrails || [])];
  }

  hasRocketTrail(unitName, styleId) {
    return this._rocketTrailSet.has(`${unitName}|${styleId}`);
  }

  addRocketTrail(unitName, styleId, styleName = '', hex = '') {
    if (!isRocketTrailUnit(unitName)) {
      return false; // Only rocket-propelled units have an exhaust plume to tint
    }
    if (this.hasRocketTrail(unitName, styleId)) {
      return false;
    }
    if (!Array.isArray(this._state.rocketTrails)) {
      this._state.rocketTrails = [];
    }
    this._state.rocketTrails.push({
      unitName,
      styleId,
      styleName,
      hex,
      unlockedAt: new Date().toISOString()
    });
    this._rocketTrailSet.add(`${unitName}|${styleId}`);
    if (!this._state.equippedRocketTrails) this._state.equippedRocketTrails = {};
    if (!this._state.equippedRocketTrails[unitName]) {
      this._state.equippedRocketTrails[unitName] = styleId;
    }
    this._save();
    progress.recordUniquePerkUnlocked();
    return true;
  }

  equipRocketTrail(unitName, styleId) {
    if (!isRocketTrailUnit(unitName)) return;
    if (!this._state.equippedRocketTrails) this._state.equippedRocketTrails = {};
    this._state.equippedRocketTrails[unitName] = styleId || null;
    this._save();
  }

  getEquippedRocketTrail(unitName) {
    if (!isRocketTrailUnit(unitName)) return null;
    return this._state.equippedRocketTrails?.[unitName] || null;
  }

  hasAnyCustomizationForUnit(unitName) {
    if (!unitName) return false;
    const prefix = `${unitName}|`;
    for (const key of this._bulletColorSet) {
      if (key.startsWith(prefix)) return true;
    }
    for (const key of this._unitAuraSet) {
      if (key.startsWith(prefix)) return true;
    }
    for (const key of this._rocketTrailSet) {
      if (key.startsWith(prefix)) return true;
    }
    for (const key of this._specialCardsSet) {
      if (key.startsWith(prefix)) return true;
    }
    return false;
  }

  // --- Booster Packs Inventory ---
  getBoosterPacks() {
    return typeof this._state.boosterPacks === 'number' ? this._state.boosterPacks : 0;
  }

  addBoosterPacks(count = 1) {
    const validCount = Math.max(0, parseInt(count, 10) || 0);
    this._state.boosterPacks = this.getBoosterPacks() + validCount;
    this._save();
    return this._state.boosterPacks;
  }

  consumeBoosterPack() {
    if (this.getBoosterPacks() > 0) {
      this._state.boosterPacks -= 1;
      this._save();
      return true;
    }
    return false;
  }

  // --- Stats & Pack/Slot Tracking ---
  recordPackOpened(ticketsWon = 0, specialCardsWon = 0) {
    this._state.stats.packsOpened = (this._state.stats.packsOpened || 0) + 1;
    this._state.stats.ticketsFound = (this._state.stats.ticketsFound || 0) + ticketsWon;
    this._save();
  }

  recordSlotSpun(isDuplicate = false) {
    this._state.stats.slotsSpun = (this._state.stats.slotsSpun || 0) + 1;
    if (isDuplicate) {
      this._state.stats.duplicatesRefunded = (this._state.stats.duplicatesRefunded || 0) + 1;
    }
    this._save();
  }

  getStats() {
    return { ...this._state.stats };
  }

  // --- Taunts / Emotes (Hearthstone-Style Commander Taunts) ---
  getTaunts() {
    return [...(this._state.taunts || ['hello', 'gotchya', 'revenge'])];
  }

  getEquippedTaunts() {
    return [...(this._state.equippedTaunts || ['hello', 'gotchya', 'revenge'])];
  }

  hasTaunt(tauntId) {
    if (!tauntId) return false;
    return this._tauntSet.has(tauntId);
  }

  unlockTaunt(tauntId) {
    if (!tauntId || this._tauntSet.has(tauntId)) return false;
    if (!Array.isArray(this._state.taunts)) this._state.taunts = [];
    this._state.taunts.push(tauntId);
    this._tauntSet.add(tauntId);
    this._save();
    progress.recordUniquePerkUnlocked();
    return true;
  }

  equipTaunt(slotIndex, tauntId) {
    if (!Array.isArray(this._state.equippedTaunts)) {
      this._state.equippedTaunts = ['hello', 'gotchya', 'revenge'];
    }
    if (slotIndex >= 0 && slotIndex < MAX_EQUIPPED_TAUNTS && this.hasTaunt(tauntId)) {
      this._state.equippedTaunts[slotIndex] = tauntId;
      this._save();
      return true;
    }
    return false;
  }

  // Replaces the whole equipped-taunt loadout at once - the Arsenal's
  // multiselect Taunts tab (js/ui/showroomModal.js) toggles chips in/out of
  // this set directly rather than addressing fixed slot indices like
  // equipTaunt() above. Requires 1-MAX_EQUIPPED_TAUNTS unique, owned taunts;
  // rejects (returns false, no state change) otherwise so a bad call can
  // never leave the player with zero or duplicate equipped taunts.
  setEquippedTaunts(tauntIds) {
    if (!Array.isArray(tauntIds)) return false;
    const unique = [...new Set(tauntIds)];
    if (unique.length < 1 || unique.length > MAX_EQUIPPED_TAUNTS) return false;
    if (!unique.every(id => this.hasTaunt(id))) return false;
    this._state.equippedTaunts = unique;
    this._save();
    return true;
  }

  // --- Profile Artworks (Commander avatar card artworks won via Slot Machine) ---
  getProfileArtworks() {
    return [...(this._state.profileArtworks || ['Hyper Marine'])];
  }

  hasProfileArtwork(cardName) {
    if (!cardName) return false;
    return this._profileArtworkSet.has(cardName);
  }

  addProfileArtwork(cardName) {
    if (!cardName || this.hasProfileArtwork(cardName)) {
      return false;
    }
    if (!Array.isArray(this._state.profileArtworks)) {
      this._state.profileArtworks = ['Hyper Marine'];
    }
    this._state.profileArtworks.push(cardName);
    this._profileArtworkSet.add(cardName);
    if (!this._state.equippedProfileArtwork) {
      this._state.equippedProfileArtwork = cardName;
    }
    this._save();
    progress.recordUniquePerkUnlocked();
    return true;
  }

  equipProfileArtwork(cardName) {
    if (cardName && !this.hasProfileArtwork(cardName)) return false;
    this._state.equippedProfileArtwork = cardName || null;
    this._save();
    return true;
  }

  getEquippedProfileArtwork() {
    return this._state.equippedProfileArtwork || 'Hyper Marine';
  }

  resetInventory(boosterPacks = 0) {
    this._state = {
      specialCards: [],
      bulletColors: [],
      equippedBulletColors: {},
      unitAuras: [],
      equippedUnitAuras: {},
      frames: [],
      equippedFrame: null,
      hpBarColors: [],
      equippedHpBarColor: null,
      avatarBorders: [],
      equippedAvatarBorder: null,
      rocketTrails: [],
      equippedRocketTrails: {},
      trayColors: [],
      equippedTrayColor: null,
      deckColors: [],
      equippedDeckColor: null,
      deckIcons: [...STARTER_DECK_ICONS],
      equippedDeckIcon: 'generic-a',
      deckbuilderTheme: 'glass',
      unlockedDeckbuilderThemes: ['glass'],
      cardTheme: 'glass',
      unlockedCardThemes: ['glass'],
      disabledSpecialCards: {},
      taunts: ['hello', 'gotchya', 'revenge'],
      equippedTaunts: ['hello', 'gotchya', 'revenge'],
      profileArtworks: ['Hyper Marine'],
      equippedProfileArtwork: 'Hyper Marine',
      boosterPacks: boosterPacks,
      stats: {
        packsOpened: 0,
        ticketsFound: 0,
        specialCardsFound: 0,
        slotsSpun: 0,
        duplicatesRefunded: 0
      },
      version: 15
    };
    this._bulletColorSet = new Set();
    this._unitAuraSet = new Set();
    this._frameSet = new Set();
    this._hpBarColorSet = new Set();
    this._avatarBorderSet = new Set();
    this._trayColorSet = new Set();
    this._deckColorSet = new Set();
    this._deckIconSet = new Set(STARTER_DECK_ICONS);
    this._deckbuilderThemeSet = new Set(['glass']);
    this._rocketTrailSet = new Set();
    this._tauntSet = new Set(this._state.taunts);
    this._profileArtworkSet = new Set(this._state.profileArtworks);
    this._disabledSpecialCardsSet = new Set();
    this._specialCardsSet = new Set();
    this._save();
    return this._state;
  }
}

export const inventory = new InventoryManager();
export { InventoryManager };
